import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const fmt = (n) => (n || 0).toLocaleString('pl-PL');

const UsageTable = ({ usage }) => (
  <div className="overflow-x-auto rounded-md border border-[var(--crm-border)]">
    <Table>
      <TableHeader>
        <TableRow className="border-[var(--crm-border)]">
          {['Użytkownik', 'Wiadomości', 'Tokeny wejście', 'Tokeny wyjście', 'Tokeny razem', 'Ostatnio'].map((h) => (
            <TableHead key={h} className="text-[var(--crm-text-secondary)] whitespace-nowrap">{h}</TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {usage.users.map((u) => (
          <TableRow key={u.user_id} className="border-[var(--crm-border)]" data-testid={`ai-usage-row-${u.user_id}`}>
            <TableCell className="text-[var(--crm-text-primary)]">{u.initials} <span className="text-[var(--crm-text-muted)] text-xs">{u.email}</span></TableCell>
            <TableCell className="font-mono text-[var(--crm-text-primary)]">{fmt(u.messages)}</TableCell>
            <TableCell className="font-mono text-[var(--crm-text-secondary)]">{fmt(u.prompt_tokens)}</TableCell>
            <TableCell className="font-mono text-[var(--crm-text-secondary)]">{fmt(u.completion_tokens)}</TableCell>
            <TableCell className="font-mono font-semibold text-[var(--crm-text-primary)]">{fmt(u.total_tokens)}</TableCell>
            <TableCell className="text-xs text-[var(--crm-text-muted)]">{u.last_used ? new Date(u.last_used).toLocaleString('pl-PL') : '–'}</TableCell>
          </TableRow>
        ))}
        <TableRow className="border-[var(--crm-border)] font-semibold" data-testid="ai-usage-total-row">
          <TableCell className="text-[var(--crm-text-primary)]">RAZEM</TableCell>
          <TableCell className="font-mono text-[var(--crm-text-primary)]" data-testid="ai-usage-total-messages">{fmt(usage.total_messages)}</TableCell>
          <TableCell className="font-mono text-[var(--crm-text-primary)]">{fmt(usage.total_prompt_tokens)}</TableCell>
          <TableCell className="font-mono text-[var(--crm-text-primary)]">{fmt(usage.total_completion_tokens)}</TableCell>
          <TableCell className="font-mono text-[var(--crm-text-primary)]" data-testid="ai-usage-total-tokens">{fmt(usage.total_tokens)}</TableCell>
          <TableCell />
        </TableRow>
      </TableBody>
    </Table>
  </div>
);

export const AIAssistantSettings = () => {
  const [limit, setLimit] = useState('');
  const [usage, setUsage] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/settings/ai-assistant`).then((r) => setLimit(String(r.data.daily_limit))).catch(() => {});
    axios.get(`${API_URL}/ai/usage`).then((r) => setUsage(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API_URL}/settings/ai-assistant`, { daily_limit: Number(limit) });
      setLimit(String(r.data.daily_limit));
      toast.success('Zapisano limit asystenta AI');
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Limit musi być liczbą całkowitą 0–10000');
    } finally { setSaving(false); }
  };

  return (
    <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] text-[var(--crm-text-primary)]" data-testid="ai-settings-card">
      <CardHeader>
        <div className="flex items-center gap-3">
          <Sparkles className="w-6 h-6 text-[#FF6B35]" />
          <div>
            <CardTitle>Asystent AI</CardTitle>
            <CardDescription className="text-[var(--crm-text-secondary)]">
              Limit wiadomości i zużycie modelu Gemini{usage ? ` (${usage.month})` : ''}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-xs text-[var(--crm-text-secondary)] block mb-1">Limit wiadomości dziennie na użytkownika</label>
            <Input type="number" min={0} max={10000} value={limit} onChange={(e) => setLimit(e.target.value)} data-testid="ai-daily-limit-input"
              className="w-40 bg-[var(--crm-bg-panel)] border-[var(--crm-border)] text-[var(--crm-text-primary)]" />
          </div>
          <Button onClick={save} disabled={saving || limit === ''} data-testid="ai-daily-limit-save-btn" className="bg-[#FF6B35] hover:bg-[var(--crm-accent-hover)] text-white">
            {saving ? 'Zapisywanie...' : 'Zapisz limit'}
          </Button>
        </div>
        <div>
          <p className="text-sm font-semibold mb-2">Zużycie w bieżącym miesiącu</p>
          {!usage ? <p className="text-sm text-[var(--crm-text-muted)]">Ładowanie...</p>
            : usage.users.length === 0 ? <p className="text-sm text-[var(--crm-text-muted)]" data-testid="ai-usage-empty">Brak użycia asystenta w tym miesiącu.</p>
              : <UsageTable usage={usage} />}
        </div>
      </CardContent>
    </Card>
  );
};

export default AIAssistantSettings;
